import { useCallback } from 'react';
import { domainTrace } from '~/domain/diagnostics/domainTrace';
import { DomainError } from '~/domain/errors/DomainError';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { OptimizationObjective } from '~/domain/patient/optimizationObjective';
import { useSetDoseDisplaySettings } from '~/hooks/dose/useDoseDisplaySettings';
import { isValidNormalizationGy } from '~/utils/dose/doseDisplayValidation';
import { enqueueSnackbar } from '~/utils/notifications';
import { DoseTargetData } from '~/utils/transforms/doseTargetsTransforms';

export interface PredictedDoseSettings {
    objectives?: OptimizationObjective[];
    targets?: DoseTargetData[];
    /**
     * Prescription used as the 100 % normalization, in Gy. All non-positive
     * or absent values fall back to the dose's default normalization (its own
     * maximum) rather than normalizing to a meaningless value, but they are
     * surfaced differently: `null` / `undefined` mean "none supplied" and are
     * applied silently, whereas `0` (or any non-finite / negative value the
     * worker returns) is treated as an invalid prescription and warned about,
     * since it is a value the prediction actively produced.
     */
    normalizationValueGy?: number | null;
}

/**
 * Applies the settings that come back with a dose-prediction job to the
 * doses just imported from its results.
 *
 * Targets and objectives are plain attached metadata on the dose entity
 * (untracked, set-once), and the display normalization is UI state in the
 * per-dose display store — so none of this is a tracked mutation and there
 * is no command/batch/session ceremony. A rejected prescription is
 * surfaced (never silently applied) per the CLAUDE.md rule.
 */
export function useApplyPredictedDoseSettings(): (
    doses: readonly DoseMatrix[],
    settings: PredictedDoseSettings
) => void {
    const setDisplaySettings = useSetDoseDisplaySettings();

    return useCallback(
        (doses: readonly DoseMatrix[], settings: PredictedDoseSettings) => {
            const requestedNormalization = settings.normalizationValueGy;
            // Same predicate the display store applies, not a second copy of
            // the rule: if the two ever drifted, this hook would accept a
            // prescription the store then dropped — the silent drop the
            // warning below exists to prevent.
            const normalizationValueGy =
                requestedNormalization != null &&
                isValidNormalizationGy(requestedNormalization)
                    ? requestedNormalization
                    : undefined;

            // CLAUDE.md: never silently drop or "fix" a clinical value. A
            // rejected prescription means the dose falls back to normalizing
            // against its own maximum, which changes every
            // percent-of-prescription readout — the user must be told.
            if (
                requestedNormalization != null &&
                normalizationValueGy === undefined
            ) {
                domainTrace.service(
                    'useApplyPredictedDoseSettings: prescription rejected',
                    { finite: Number.isFinite(requestedNormalization) }
                );
                enqueueSnackbar(
                    'The predicted prescription dose was not usable. Dose is ' +
                        'normalized to its maximum instead — set the ' +
                        'normalization manually before relying on % values.',
                    { variant: 'warning' }
                );
            }

            // Empty is "nothing supplied", for both fields alike. Writing an
            // empty array would latch the set-once entity field and lock the
            // dose out of a later real prescription — the asymmetry the
            // length check already avoided for objectives.
            const objectives =
                settings.objectives && settings.objectives.length > 0
                    ? settings.objectives
                    : undefined;
            const targets =
                settings.targets && settings.targets.length > 0
                    ? settings.targets
                    : undefined;

            for (const dose of doses) {
                // Check both latches BEFORE writing either. The two writes
                // are not individually reversible, so committing objectives
                // and then throwing on targets would leave the dose half
                // carrying this prediction and half carrying the previous
                // one, under a warning saying nothing was applied.
                const alreadySet =
                    (objectives !== undefined &&
                        dose.optimizationObjectives !== undefined) ||
                    (targets !== undefined && dose.targets !== undefined);

                if (alreadySet) {
                    // Set-once ingest metadata is already present. Report it
                    // rather than tearing down the render tree mid-import:
                    // the earlier prescription is intact and still the one on
                    // screen, but the user must know this result was not
                    // applied — including its normalization, which is skipped
                    // below so percent-of-prescription keeps referencing the
                    // prescription the detail view actually shows.
                    domainTrace.service(
                        'useApplyPredictedDoseSettings: metadata already set',
                        { doseIsNew: dose.isNew }
                    );
                    enqueueSnackbar(
                        'This dose already carries a prescription from an ' +
                            'earlier prediction; the new one was not applied. ' +
                            'Reload the dose to use the latest result.',
                        { variant: 'warning' }
                    );
                    continue;
                }

                try {
                    if (objectives) {
                        dose.setOptimizationObjectives(objectives);
                    }
                    if (targets) {
                        dose.setTargets(targets);
                    }
                } catch (e) {
                    // The pre-check above makes this unreachable, so a throw
                    // here means the entity's invariant and this hook's view
                    // of it have diverged. Surface it and skip the settings
                    // write; never let a domain error escape into the import.
                    if (!(e instanceof DomainError)) {
                        throw e;
                    }
                    domainTrace.service(
                        'useApplyPredictedDoseSettings: unexpected set-once rejection',
                        { message: e.message }
                    );
                    enqueueSnackbar(
                        'The predicted prescription could not be applied to ' +
                            'this dose. Reload the dose to use the latest ' +
                            'result.',
                        { variant: 'warning' }
                    );
                    continue;
                }

                if (normalizationValueGy !== undefined) {
                    setDisplaySettings(dose.id, { normalizationValueGy });
                }
            }
        },
        [setDisplaySettings]
    );
}
